import { useEffect } from 'react';
import { SITE } from '../siteConfig';

const INSTAGRAM_SCRIPT_ID = 'instagram-embed-script';

const InstagramProfileEmbed = () => {
  useEffect(() => {
    const processEmbeds = () => {
      window.instgrm?.Embeds?.process();
    };

    const existingScript = document.getElementById(INSTAGRAM_SCRIPT_ID);
    if (existingScript) {
      processEmbeds();
      existingScript.addEventListener('load', processEmbeds);
      return () => existingScript.removeEventListener('load', processEmbeds);
    }

    const script = document.createElement('script');
    script.id = INSTAGRAM_SCRIPT_ID;
    script.async = true;
    script.src = 'https://www.instagram.com/embed.js';
    script.addEventListener('load', processEmbeds);
    document.body.appendChild(script);

    return () => script.removeEventListener('load', processEmbeds);
  }, []);

  return (
    <div className="instagram-embed-shell">
      <blockquote
        className="instagram-media"
        data-instgrm-permalink={SITE.social.instagram}
        data-instgrm-version="14"
      >
        <a
          href={SITE.social.instagram}
          target="_blank"
          rel="noreferrer"
        >
          View Patrick&apos;s Bar on Instagram
        </a>
      </blockquote>
    </div>
  );
};

const FacebookTimelineEmbed = () => {
  const src = new URL('https://www.facebook.com/plugins/page.php');
  src.searchParams.set('href', SITE.social.facebook);
  src.searchParams.set('tabs', 'timeline');
  src.searchParams.set('width', '500');
  src.searchParams.set('height', '620');
  src.searchParams.set('small_header', 'true');
  src.searchParams.set('adapt_container_width', 'true');
  src.searchParams.set('hide_cover', 'false');
  src.searchParams.set('show_facepile', 'false');

  return (
    <iframe
      className="facebook-embed"
      title="Latest posts from Patrick's Bar on Facebook"
      src={src.toString()}
      width="500"
      height="620"
      loading="lazy"
      allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
    />
  );
};

const SocialWall = ({ preference, onPreferenceChange }) => {
  const embedsEnabled = preference === 'enabled';

  return (
    <section id="social" className="section section--social" aria-labelledby="social-title">
      <div className="section-heading section-heading--light">
        <p className="eyebrow">From behind the bar</p>
        <h2 id="social-title">See what&apos;s happening at Patrick&apos;s</h2>
        <p>
          Music nights, sport, local characters and whatever has appeared on the
          bar since yesterday.
        </p>
      </div>

      {!embedsEnabled ? (
        <div className="social-consent-card">
          <div>
            <p className="social-consent-card__label">Live social posts are paused</p>
            <h3>Load Facebook and Instagram here?</h3>
            <p>
              Enabling these panels connects your browser directly to Meta. Their
              services may set cookies or collect usage information. The rest of
              this website works without them.
            </p>
          </div>
          <div className="social-consent-card__actions">
            <button
              type="button"
              className="button button--cream"
              onClick={() => onPreferenceChange('enabled')}
            >
              Load social posts
            </button>
            {preference !== 'disabled' && (
              <button
                type="button"
                className="button button--ghost-light"
                onClick={() => onPreferenceChange('disabled')}
              >
                Keep them off
              </button>
            )}
          </div>
          {preference === 'disabled' && (
            <p className="social-consent-card__status" role="status">
              Social posts are off. You can change this from the footer.
            </p>
          )}
        </div>
      ) : (
        <>
          <div className="social-grid">
            <article className="social-panel">
              <header className="social-panel__header">
                <div>
                  <p className="social-panel__platform">Facebook</p>
                  <h3>Latest updates</h3>
                </div>
                <a
                  href={SITE.social.facebook}
                  target="_blank"
                  rel="noreferrer"
                  className="text-link"
                >
                  Open page <span aria-hidden="true">↗</span>
                </a>
              </header>
              <div className="social-panel__embed social-panel__embed--facebook">
                <FacebookTimelineEmbed />
              </div>
            </article>

            <article className="social-panel">
              <header className="social-panel__header">
                <div>
                  <p className="social-panel__platform">Instagram</p>
                  <h3>Photos from the pub</h3>
                </div>
                <a
                  href={SITE.social.instagram}
                  target="_blank"
                  rel="noreferrer"
                  className="text-link"
                >
                  Open profile <span aria-hidden="true">↗</span>
                </a>
              </header>
              <div className="social-panel__embed social-panel__embed--instagram">
                <InstagramProfileEmbed />
              </div>
            </article>
          </div>

          <div className="social-settings-row">
            <p>These live panels are supplied by Facebook and Instagram.</p>
            <button
              type="button"
              className="text-button text-button--light"
              onClick={() => onPreferenceChange('disabled')}
            >
              Turn social posts off
            </button>
          </div>
        </>
      )}
    </section>
  );
};

export default SocialWall;
